import "globalSetUp";

import { useEffect } from "react";
import { IonApp, isPlatform } from "@ionic/react";
import { IonReactHashRouter, IonReactRouter } from "@ionic/react-router";
import { ErrorBoundary, setUser as SentryReactSetUser } from "@sentry/react";
import { FeedbackModalDisplayProvider } from "context/FeedbackModalDisplay";
import { IntercomMessenger } from "IntercomMessenger";
import { useAuth } from "pages";
import { DesktopStack, MobileStack, UnAuthedRoutes } from "routes";
import { LoadingInterstitial } from "swing-components";
import { usePendo } from "utils/hooks";

import { BUILD_PROFILE } from "~utils";

declare const window: {
  FS: (arg1: string, arg2: Record<string, unknown>) => void;
  localStorage: Storage;
};

/********** Component **********/
/**
 * Swing Schools application entry point.
 */
export default function App() {
  const { userInfo, status, isAuthenticating } = useAuth();
  const { initPendo } = usePendo();

  // store authentication status in localStorage for `LiveUpdate` to check since it
  // does not have access to `useAuth` hook as this must be inside of `AuthProvider`
  useEffect(() => {
    window.localStorage.setItem("isAuthenticating", isAuthenticating ? "true" : "false");
  }, [isAuthenticating]);

  useEffect(() => {
    if (userInfo.id === undefined) return;

    // set Sentry when user exists
    // https://docs.sentry.io/platforms/javascript/guides/react/session-replay/configuration
    SentryReactSetUser({ id: userInfo.id, email: userInfo.email });

    if (BUILD_PROFILE !== "prod") return;

    // set Pendo when user exists and in prod
    // https://agent.pendo.io/
    initPendo({ userId: userInfo.id });
  }, [initPendo, userInfo.email, userInfo.id]);

  useEffect(() => {
    // set FullStory user identity when user and FullStory exist
    if (userInfo.dbid && window.FS) {
      window.FS("setIdentity", {
        uid: userInfo.dbid,
        properties: {
          id: userInfo.id,
          displayName: userInfo.name,
          email: userInfo.email,
          admin: userInfo.roles.includes("ROLE_ADMIN"),
        },
      });
    }
  }, [userInfo]);

  /***** Render *****/
  return (
    <ErrorBoundary fallback={<p>Something went wrong</p>}>
      <IonApp>
        {/* TODO: https://swingeducation.atlassian.net/browse/PDE-2219 has been created to remove hash routing. */}
        {status === undefined && (
          <IonReactHashRouter>
            <div
              css={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoadingInterstitial />
            </div>
          </IonReactHashRouter>
        )}
        {status === "AUTHENTICATED" && (
          <IonReactHashRouter>
            <FeedbackModalDisplayProvider>
              {isPlatform("capacitor") ? (
                <>
                  <IntercomMessenger />
                  <MobileStack />
                </>
              ) : isPlatform("desktop") ? (
                <>
                  <IntercomMessenger />
                  <DesktopStack />
                </>
              ) : (
                <>
                  <IntercomMessenger />
                  <MobileStack />
                </>
              )}
            </FeedbackModalDisplayProvider>
          </IonReactHashRouter>
        )}
        {status === "UN_AUTHENTICATED" && (
          <IonReactRouter>
            <UnAuthedRoutes />
          </IonReactRouter>
        )}
      </IonApp>
    </ErrorBoundary>
  );
}
